import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ═══════════════════════════════════════════════════════════════════════════
// SHARED CONSTANTS — Windows/Doors + Mill Work intake PDF palette
// ═══════════════════════════════════════════════════════════════════════════
const BROWN       = [139, 69, 19];
const BROWN_LIGHT = [200, 160, 110];
const CREAM       = [252, 248, 244];
const DARK        = [25, 22, 18];
const GRAY        = [120, 110, 100];
const GRAY_LIGHT  = [220, 215, 208];
const WHITE       = [255, 255, 255];
const TEAL        = [0, 105, 92];
const TEAL_BG     = [224, 247, 242];
const GREEN_BG    = [247, 250, 244];
const GREEN_BAR   = [106, 154, 74];
const NAVY        = [45, 55, 72];

// ═══════════════════════════════════════════════════════════════════════════
// REPAIR PDF CONSTANTS — matched to estimatePDF.js exactly
// ═══════════════════════════════════════════════════════════════════════════
const R_BROWN       = [101, 48, 10];
const R_BROWN_LIGHT = [180, 120, 70];
const R_CREAM       = [252, 249, 245];
const R_GRAY_LINE   = [220, 215, 208];
const R_GRAY_TEXT   = [100, 95, 90];
const R_GRAY_MID    = [150, 145, 140];
const R_DARK        = [25, 22, 18];
const R_HEAD_BG     = [242, 236, 228];
const R_HEAD_TEXT   = [70, 35, 8];
const R_ROW_ALT     = [250, 247, 243];
const R_TERM_BG     = [245, 239, 230];

const R_PAGE_W    = 216;
const R_PAGE_H    = 279;
const R_ML        = 14;
const R_MR        = 14;
const R_CONTENT_W = R_PAGE_W - R_ML - R_MR;
const R_HDR_H     = 36;
const R_FTR_H     = 22;
const R_FTR_Y     = R_PAGE_H - R_FTR_H;

const PAGE_W = 215.9;
const PAGE_H = 279.4;
const ML     = 14;
const MR     = 14;
const CW     = PAGE_W - ML - MR;

const TWP_LOGO_URL      = '/brand/twp-logo.png';
const MILLWORK_LOGO_URL = '/brand/sdmillwork-logo.png';

// ═══════════════════════════════════════════════════════════════════════════
// ROUTING CONFIG
// ═══════════════════════════════════════════════════════════════════════════
function routeFor(intake) {
  switch (intake.inquiryType) {
    case 'mill_work':
      return {
        key:        'mill_work',
        logoUrl:    MILLWORK_LOGO_URL,
        brandName:  'SD Millwork',
        badgeLabel: 'MILL WORK',
        brandColor: NAVY,
        rep:        'Mike',
        bannerTitle: (rep) => rep + ' \u2014 This inquiry is routed to you.',
        bannerBody:  (customer, needsCall) => needsCall
          ? 'Please call ' + (customer || 'the customer') + ' at your earliest convenience.'
          : 'Please follow up with ' + (customer || 'this customer') + ' when available.',
      };
    case 'repair':
      return {
        key:        'repair',
        logoUrl:    TWP_LOGO_URL,
        brandName:  'The Window Place LLC',
        badgeLabel: 'REPAIR ORDER',
        brandColor: R_BROWN,
        rep:        'Justin',
        bannerTitle: (rep) => rep + ' \u2014 Repair request (print only).',
        bannerBody:  (customer) =>
          'Please review this repair for ' + (customer || 'this customer') +
          ' and follow up when available. No email was sent \u2014 this form was printed and handed to you.',
      };
    case 'windows_doors':
    default:
      return {
        key:        'windows_doors',
        logoUrl:    TWP_LOGO_URL,
        brandName:  'The Window Place LLC',
        badgeLabel: 'WINDOWS & DOORS',
        brandColor: BROWN,
        rep:        'AJ',
        bannerTitle: (rep) => rep + ' \u2014 This inquiry is routed to you.',
        bannerBody:  (customer, needsCall) => needsCall
          ? 'Please call ' + (customer || 'the customer') + ' at your earliest convenience.'
          : 'Please follow up with ' + (customer || 'this customer') + ' when available.',
      };
  }
}

async function getLogo(url) {
  try {
    const res  = await fetch(url);
    const blob = await res.blob();
    return new Promise(resolve => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result);
      fr.onerror   = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function getPhones(intake) {
  return [
    intake.phoneMobile ? { label: 'Mobile', val: intake.phoneMobile } : null,
    intake.phoneOffice ? { label: 'Office', val: intake.phoneOffice } : null,
    intake.phoneOther  ? { label: 'Other',  val: intake.phoneOther  } : null,
  ].filter(Boolean);
}

function getFlags(intake) {
  const map = {
    requestTelephoned:    'Telephoned',
    requestReturnCall:    'Return your call',
    requestPleaseCall:    'Please call',
    requestWillCallAgain: 'Will call again',
    requestCameToSeeYou:  'Came to see you',
    requestWantsToSeeYou: 'Wants to see you',
  };
  return Object.entries(map).filter(([k]) => intake[k]).map(([, v]) => v);
}

function sectionLabel(doc, text, x, y) {
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BROWN_LIGHT);
  doc.text(text, x, y);
}

// ─────────────────────────────────────────────────────────────────────────
// Set font BEFORE measuring so splitTextToSize wraps accurately
// ─────────────────────────────────────────────────────────────────────────
function wrapText(doc, text, maxWidth, fontSize, fontStyle) {
  doc.setFont('helvetica', fontStyle || 'normal');
  doc.setFontSize(fontSize);
  return doc.splitTextToSize(text, maxWidth);
}

// ─────────────────────────────────────────────────────────────────────────
// Repair-type + Best-time helpers: read from intake, produce display strings
// ─────────────────────────────────────────────────────────────────────────
function buildRepairTypeString(intake) {
  const rt = intake.repairTypes || {};
  const opts = [
    { key: 'windows', label: 'Windows' },
    { key: 'siding',  label: 'Siding'  },
    { key: 'door',    label: 'Door'    },
    { key: 'other',   label: 'Other'   },
  ];
  // Use [x] for checked, [ ] for unchecked — works in plain text cells
  return opts.map(o => `${rt[o.key] ? '[x]' : '[ ]'} ${o.label}`).join('    ');
}

function buildBestTimeString(intake) {
  const text = (intake.bestTimeToCall || '').trim();
  const ampm = (intake.bestTimeAmPm   || '').trim();
  if (!text && !ampm) {
    // Still show the classic AM/PM checkboxes for hand-fill backup
    return '________________    [ ] AM    [ ] PM';
  }
  // Show the filled value and [x]-mark the right half of the AM/PM
  const amMark = ampm === 'AM' ? '[x]' : '[ ]';
  const pmMark = ampm === 'PM' ? '[x]' : '[ ]';
  const left   = text || '________________';
  return `${left}    ${amMark} AM    ${pmMark} PM`;
}

// ═══════════════════════════════════════════════════════════════════════════
// REPAIR PDF — mirrors estimatePDF.js visual language
// ═══════════════════════════════════════════════════════════════════════════
async function generateRepairPDF(intake) {
  const doc  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const logo = await getLogo(TWP_LOGO_URL);

  const intakeDate = intake.createdAt
    ? (intake.createdAt.toDate ? intake.createdAt.toDate() : new Date(intake.createdAt))
    : new Date();
  const dateStr = intakeDate.toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  const dateStrShort = intakeDate.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  const contactLabel = { 'walk-in': 'Walk-In', phone: 'Phone Call', email: 'Email' }[intake.contactType] || 'Walk-In';

  // ── HEADER ──
  doc.setFillColor(...R_CREAM);
  doc.rect(0, 0, R_PAGE_W, R_HDR_H, 'F');
  doc.setFillColor(...R_BROWN);
  doc.rect(0, 0, 4, R_HDR_H, 'F');

  if (logo) {
    try { doc.addImage(logo, 'PNG', R_ML + 4, 4, 22, 22); } catch (e) {}
  }

  doc.setTextColor(...R_BROWN);
  doc.setFontSize(12); doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', R_ML + 30, 10);
  doc.setFontSize(6.8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  doc.text('1300 E 4th Ave  \u2022  Milbank, SD 57252-1547', R_ML + 30, 15.5);
  doc.text('Phone: (605) 432-1834  \u2022  Fax: (866) 432-1718', R_ML + 30, 20);
  doc.text('office@thewindowplacellc.com', R_ML + 30, 24.5);

  doc.setFontSize(25); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_BROWN);
  doc.text('REPAIR ORDER', R_PAGE_W - R_MR, 15, { align: 'right' });

  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  doc.text('Date: ' + dateStr, R_PAGE_W - R_MR, 21.5, { align: 'right' });

  doc.setFillColor(...R_BROWN);
  doc.rect(0, R_HDR_H - 1.5, R_PAGE_W, 1.5, 'F');

  // ── Prepared For block ──
  const CY = R_HDR_H + 5;
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text('PREPARED FOR', R_ML + 4, CY);

  const leftColMaxW = 92;
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_DARK);
  const custNameLines = doc.splitTextToSize(intake.customerName || '', leftColMaxW);
  doc.text(custNameLines[0] || '', R_ML + 4, CY + 6);

  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  let ly = CY + 11.5;
  if (intake.addressLine1) {
    const a1 = doc.splitTextToSize(intake.addressLine1, leftColMaxW);
    a1.forEach(line => { doc.text(line, R_ML + 4, ly); ly += 4.5; });
  }
  const cityState = [intake.city, intake.state, intake.zip].filter(Boolean).join(', ');
  if (cityState) {
    const cs = doc.splitTextToSize(cityState, leftColMaxW);
    cs.forEach(line => { doc.text(line, R_ML + 4, ly); ly += 4.5; });
  }

  const RX = R_PAGE_W - R_MR - 82;
  const RX_VAL = RX + 16;
  const RX_VAL_MAX_W = R_PAGE_W - R_MR - RX_VAL - 1;
  let ry_hdr = CY + 6;
  const primaryPhone = intake.phoneMobile || intake.phoneOffice || intake.phoneOther;
  if (primaryPhone) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...R_BROWN_LIGHT);
    doc.text('Phone:', RX, ry_hdr);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_DARK);
    const ph = doc.splitTextToSize(primaryPhone, RX_VAL_MAX_W);
    doc.text(ph[0] || '', RX_VAL, ry_hdr); ry_hdr += 5.5;
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text('Source:', RX, ry_hdr);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_DARK);
  doc.text(contactLabel, RX_VAL, ry_hdr); ry_hdr += 5.5;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text('Ref:', RX, ry_hdr);
  doc.setFont('helvetica', 'bolditalic'); doc.setFontSize(7.5); doc.setTextColor(...R_BROWN);
  const refLines = doc.splitTextToSize(intake.id || '', RX_VAL_MAX_W);
  doc.text(refLines[0] || '', RX_VAL, ry_hdr); ry_hdr += 5.5;

  const ruleY = Math.max(ly, ry_hdr) + 3;
  doc.setDrawColor(...R_GRAY_LINE); doc.setLineWidth(0.3);
  doc.line(R_ML, ruleY, R_PAGE_W - R_MR, ruleY);
  const contentStartY = ruleY + 4;

  // ── FOOTER ──
  doc.setFillColor(...R_CREAM);
  doc.rect(0, R_FTR_Y, R_PAGE_W, R_FTR_H, 'F');
  doc.setFillColor(...R_BROWN);
  doc.rect(0, R_FTR_Y, R_PAGE_W, 1, 'F');
  doc.rect(0, R_FTR_Y, 4, R_FTR_H, 'F');

  doc.setDrawColor(...R_BROWN_LIGHT); doc.setLineWidth(0.35);
  doc.line(R_ML + 6,  R_FTR_Y + 10, R_ML + 66,  R_FTR_Y + 10);
  doc.line(R_ML + 76, R_FTR_Y + 10, R_ML + 112, R_FTR_Y + 10);
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_MID);
  doc.text('Customer Signature', R_ML + 6,  R_FTR_Y + 14);
  doc.text('Date',                R_ML + 76, R_FTR_Y + 14);

  doc.setFontSize(8.5); doc.setFont('helvetica', 'bolditalic'); doc.setTextColor(...R_BROWN);
  doc.text('Thank you,  Justin', R_PAGE_W - R_MR, R_FTR_Y + 8, { align: 'right' });
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_MID);
  doc.text('Page 1 of 1', R_PAGE_W - R_MR, R_FTR_Y + 13, { align: 'right' });
  doc.setFontSize(6.5); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text(
    'The Window Place LLC  \u2022  (605) 432-1834  \u2022  office@thewindowplacellc.com',
    R_PAGE_W / 2, R_FTR_Y + 19, { align: 'center' }
  );

  // ── Repair Details section label ──
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text('REPAIR DETAILS', R_ML + 4, contentStartY);

  // Table values — now use REAL data from intake
  const empty = '_________________________';
  const cityStateZip = [intake.city, intake.state, intake.zip].filter(Boolean).join(' / ') || empty;
  const mobile = intake.phoneMobile || '____________';
  const home   = intake.phoneOffice || '____________';
  const work   = intake.phoneOther  || '____________';

  const repairTypeLine = buildRepairTypeString(intake);
  const bestTimeLine   = buildBestTimeString(intake);

  autoTable(doc, {
    startY: contentStartY + 3,
    head: [['Field', 'Information']],
    body: [
      ['Repair Type',       repairTypeLine],
      ['Street Address',    intake.addressLine1 || empty],
      ['City / State / ZIP', cityStateZip],
      ['Phone Numbers',     'Mobile: ' + mobile + '   Office: ' + home + '   Other: ' + work],
      ['Best Time to Call', bestTimeLine],
      ['Email',             intake.email || 'Not provided'],
    ],
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 5, right: 5 },
      textColor: R_DARK,
      lineColor: R_GRAY_LINE,
      lineWidth: 0.15,
      overflow: 'linebreak',
      valign: 'middle',
      font: 'helvetica',
    },
    headStyles: {
      fillColor: R_HEAD_BG,
      textColor: R_HEAD_TEXT,
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'left',
      lineColor: [180, 140, 100],
      lineWidth: 0.4,
      minCellHeight: 10,
      cellPadding: { top: 3, bottom: 3, left: 5, right: 5 },
    },
    alternateRowStyles: { fillColor: R_ROW_ALT },
    columnStyles: {
      0: {
        cellWidth: 50,
        halign: 'left',
        fontStyle: 'bold',
        textColor: R_BROWN,
        fontSize: 7.5,
      },
      1: { cellWidth: 'auto', halign: 'left' },
    },
    margin: { left: R_ML, right: R_MR },
    tableLineColor: R_GRAY_LINE,
    tableLineWidth: 0.15,
    didDrawRow: (data) => {
      if (data.section === 'body') {
        doc.setFillColor(...R_BROWN_LIGHT);
        doc.rect(R_ML, data.row.y, 2, data.row.height, 'F');
      }
    },
  });

  let y = doc.lastAutoTable.finalY + 6;

  // ── Description of Problem ──
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_BROWN_LIGHT);
  doc.text('DESCRIPTION OF PROBLEM', R_ML + 4, y);
  y += 3;

  const msg = intake.message || 'No description provided.';
  const msgMaxW  = R_CONTENT_W - 12;
  const msgLines = wrapText(doc, msg, msgMaxW, 8.5, 'normal');
  const lineH    = 5;
  const msgBodyH = Math.max(18, msgLines.length * lineH + 8);
  const panelH   = 9 + msgBodyH;

  doc.setFillColor(...R_BROWN);
  doc.rect(R_ML, y, R_CONTENT_W, 9, 'F');
  doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...WHITE);
  doc.text('AS REPORTED BY CUSTOMER', R_ML + 6, y + 6);

  doc.setFillColor(...R_TERM_BG);
  doc.rect(R_ML, y + 9, R_CONTENT_W, msgBodyH, 'F');

  doc.setDrawColor(...R_BROWN_LIGHT); doc.setLineWidth(0.3);
  doc.rect(R_ML, y, R_CONTENT_W, panelH, 'S');

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...R_DARK);
  let textY = y + 9 + 5;
  msgLines.forEach(line => {
    doc.text(line, R_ML + 6, textY);
    textY += lineH;
  });

  y += panelH + 8;

  // ── Sign-off ──
  doc.setDrawColor(...R_GRAY_LINE); doc.setLineWidth(0.3);
  doc.line(R_ML, y, R_PAGE_W - R_MR, y); y += 5;

  const boxW = 86;
  const boxX = R_PAGE_W - R_MR - boxW;
  const leftW = boxX - R_ML - 8;

  const followText = 'Please follow up with the customer at your earliest convenience.';
  const followLines = wrapText(doc, followText, leftW, 8.5, 'bold');
  doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_DARK);
  let lly = y;
  followLines.forEach(line => { doc.text(line, R_ML, lly); lly += 4.5; });

  const noteText = 'Contact the office if any details on this record need to be corrected before work begins.';
  const noteLines = wrapText(doc, noteText, leftW, 8, 'normal');
  lly += 1.5;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...R_GRAY_TEXT);
  noteLines.forEach(line => { doc.text(line, R_ML, lly); lly += 4.2; });

  lly += 3;
  doc.setDrawColor(...R_GRAY_LINE); doc.setLineWidth(0.2);
  doc.line(R_ML, lly, R_ML + leftW, lly); lly += 4;

  doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_DARK);
  doc.text('Thank you,', R_ML, lly); lly += 5;
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_BROWN);
  doc.text('Justin', R_ML, lly); lly += 4.5;
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT); doc.setFontSize(7.5);
  doc.text('Repair & Service', R_ML, lly);

  // ── Routing box on the right ──
  let ry = y;
  const rowH = 8;

  doc.setFillColor(...WHITE);
  doc.setDrawColor(...R_GRAY_LINE); doc.setLineWidth(0.2);
  doc.rect(boxX, ry, boxW, rowH, 'FD');
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  doc.text('Request Type', boxX + 6, ry + 5.5);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_DARK);
  doc.text('Repair / Warranty', boxX + boxW - 5, ry + 5.5, { align: 'right' });
  ry += rowH;

  doc.setFillColor(248, 245, 240);
  doc.rect(boxX, ry, boxW, rowH, 'FD');
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  doc.text('Date Received', boxX + 6, ry + 5.5);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_DARK);
  doc.text(dateStrShort, boxX + boxW - 5, ry + 5.5, { align: 'right' });
  ry += rowH;

  doc.setFillColor(248, 245, 240);
  doc.rect(boxX, ry, boxW, rowH, 'FD');
  doc.setFont('helvetica', 'normal'); doc.setTextColor(...R_GRAY_TEXT);
  doc.text('Contact Method', boxX + 6, ry + 5.5);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_DARK);
  doc.text(contactLabel, boxX + boxW - 5, ry + 5.5, { align: 'right' });
  ry += rowH;

  doc.setFillColor(...R_BROWN);
  doc.rect(boxX, ry, boxW, 12, 'F');
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...WHITE);
  doc.text('ASSIGNED TO', boxX + 6, ry + 8.5);
  doc.text('JUSTIN', boxX + boxW - 5, ry + 8.5, { align: 'right' });

  doc.setDrawColor(...R_BROWN_LIGHT); doc.setLineWidth(0.5);
  doc.rect(boxX, y, boxW, rowH * 3 + 12, 'S');

  return doc.output('blob');
}

// ═══════════════════════════════════════════════════════════════════════════
// STANDARD INTAKE PDF — Windows/Doors + Mill Work (UNCHANGED)
// ═══════════════════════════════════════════════════════════════════════════
async function generateStandardIntakePDF(intake) {
  const route   = routeFor(intake);
  const logo    = await getLogo(route.logoUrl);
  const doc     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const phones  = getPhones(intake);
  const flags   = getFlags(intake);
  const isMillwork = route.key === 'mill_work';

  const addr = [
    intake.addressLine1,
    intake.addressLine2,
    [intake.city, intake.state, intake.zip].filter(Boolean).join(', '),
  ].filter(Boolean).join('\n');

  const contactLabel = { 'walk-in': 'Walk-In', phone: 'Phone Call', email: 'Email' }[intake.contactType] || 'Walk-In';

  const intakeDate = intake.createdAt
    ? (intake.createdAt.toDate ? intake.createdAt.toDate() : new Date(intake.createdAt))
    : new Date();
  const dateStr = intakeDate.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const timeStr = intakeDate.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  doc.setFillColor(...CREAM);
  doc.rect(0, 0, PAGE_W, 34, 'F');
  doc.setFillColor(...route.brandColor);
  doc.rect(0, 34, PAGE_W, 1.5, 'F');
  doc.rect(0, 0, 5, 34, 'F');

  if (logo) { try { doc.addImage(logo, 'PNG', ML + 5, 6, 20, 20); } catch {} }

  doc.setTextColor(...route.brandColor);
  doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text(route.brandName, ML + 29, 13);

  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...GRAY);
  if (isMillwork) {
    doc.text('1300 E. 4th Ave  \u2022  Milbank, South Dakota 57252', ML + 29, 18.5);
    doc.text('(605) 432-1834  \u2022  Toll Free (877) 224-0745  \u2022  Fax (866) 432-1718', ML + 29, 23);
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GRAY);
    doc.text('OFFICE INTAKE', ML + 29, 27);
    doc.setFontSize(7); doc.setFont('helvetica', 'normal');
    doc.text(contactLabel, ML + 29, 31.5);
  } else {
    doc.text('1300 E 4th Ave  \u2022  Milbank, SD 57252  \u2022  (605) 432-1834  \u2022  office@thewindowplacellc.com', ML + 29, 18.5);
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GRAY);
    doc.text('OFFICE INTAKE', ML + 29, 24);
    doc.setFontSize(7); doc.setFont('helvetica', 'normal');
    doc.text(contactLabel, ML + 29, 29.5);
  }

  doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
  const badgeTextW = doc.getTextWidth(route.badgeLabel);
  const badgeW     = Math.max(42, badgeTextW + 10);
  const badgeX     = PAGE_W - MR - badgeW;
  doc.setFillColor(...route.brandColor);
  doc.roundedRect(badgeX, 7, badgeW, 11, 3, 3, 'F');
  doc.setTextColor(...WHITE);
  doc.text(route.badgeLabel, badgeX + badgeW / 2, 14, { align: 'center' });

  let y = 42;

  doc.setFillColor(...TEAL_BG);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.4);
  doc.roundedRect(ML, y, CW, 16, 3, 3, 'FD');
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL);
  doc.text('DATE OF INTAKE', ML + 5, y + 6);
  doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text(dateStr, ML + 5, y + 13);
  doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(...TEAL);
  doc.text(timeStr, PAGE_W - MR - 5, y + 13, { align: 'right' });
  y += 22;

  doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN_LIGHT);
  doc.text('CUSTOMER', ML, y);
  y += 11;
  const customerName = intake.customerName || '\u2014';
  let nameFontSize = 28;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(nameFontSize);
  while (doc.getTextWidth(customerName) > CW - 4 && nameFontSize > 14) {
    nameFontSize -= 1; doc.setFontSize(nameFontSize);
  }
  doc.setTextColor(...DARK);
  doc.text(customerName, ML, y);
  y += 16;

  const halfW = (CW - 6) / 2;
  const col2x = ML + halfW + 6;
  const phoneBoxH = phones.length > 0 ? 9 + phones.length * 12 : 22;

  doc.setFillColor(...TEAL_BG);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.roundedRect(ML, y, halfW, phoneBoxH, 3, 3, 'FD');
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TEAL);
  doc.text('PHONE', ML + 4, y + 6);
  if (phones.length > 0) {
    phones.forEach((p, i) => {
      const py = y + 12 + i * 12;
      doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GRAY);
      doc.text(p.label.toUpperCase(), ML + 4, py);
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN);
      doc.text(p.val, ML + 4, py + 6);
    });
  } else {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(...GRAY_LIGHT);
    doc.text('Not provided', ML + 4, y + 14);
  }

  doc.setFillColor(248, 248, 252);
  doc.setDrawColor(...GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.roundedRect(col2x, y, halfW, phoneBoxH, 3, 3, 'FD');
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...GRAY);
  doc.text('EMAIL', col2x + 4, y + 6);
  if (intake.email) {
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    const emailLines = doc.splitTextToSize(intake.email, halfW - 10);
    doc.text(emailLines, col2x + 4, y + 14);
  } else {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(...GRAY_LIGHT);
    doc.text('Not provided', col2x + 4, y + 14);
  }
  y += phoneBoxH + 8;

  let leftY = y;
  let rightY = y;

  sectionLabel(doc, 'ADDRESS', ML, leftY);
  leftY += 5;
  if (addr) {
    doc.setFontSize(9.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    const addrLines = doc.splitTextToSize(addr, halfW - 4);
    addrLines.forEach(line => { doc.text(line, ML, leftY); leftY += 5.5; });
  } else {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(...GRAY);
    doc.text('\u2014', ML, leftY); leftY += 5.5;
  }

  if (flags.length > 0) {
    sectionLabel(doc, 'MESSAGE FLAGS', col2x, rightY);
    rightY += 5;
    flags.forEach(flag => {
      doc.setFillColor(...CREAM);
      doc.setDrawColor(...BROWN_LIGHT);
      doc.setLineWidth(0.3);
      doc.roundedRect(col2x, rightY - 3.5, halfW, 8, 2, 2, 'FD');
      doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN);
      doc.text(flag, col2x + 4, rightY + 1.5);
      rightY += 10;
    });
  }

  y = Math.max(leftY, rightY) + 8;

  doc.setDrawColor(...GRAY_LIGHT); doc.setLineWidth(0.3);
  doc.line(ML, y, PAGE_W - MR, y);
  y += 7;

  if (intake.message) {
    sectionLabel(doc, 'MESSAGE & NOTES', ML, y);
    y += 5;

    const msgTextX = ML + 8;
    const msgTextW = CW - 14;
    doc.setFontSize(9.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    const msgLines = doc.splitTextToSize(intake.message, msgTextW);
    const lineH    = 5.5;
    const msgH     = msgLines.length * lineH + 12;

    doc.setFillColor(...GREEN_BG);
    doc.setDrawColor(...GREEN_BAR);
    doc.setLineWidth(0.3);
    doc.roundedRect(ML, y, CW, msgH, 2, 2, 'FD');

    doc.setFillColor(...GREEN_BAR);
    doc.rect(ML, y, 3.5, msgH, 'F');

    msgLines.forEach((line, i) => {
      doc.text(line, msgTextX, y + 7 + i * lineH);
    });

    y += msgH + 8;
  }

  const needsCall    = flags.includes('Please call') || flags.includes('Return your call');
  const bannerTitle  = route.bannerTitle(route.rep);
  const bannerBody   = route.bannerBody(intake.customerName, needsCall);

  doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
  const actionLines = doc.splitTextToSize(bannerBody, CW - 12);
  const actionBoxH  = 10 + actionLines.length * 5 + 8;

  doc.setFillColor(...CREAM);
  doc.setDrawColor(...BROWN_LIGHT);
  doc.setLineWidth(0.4);
  doc.roundedRect(ML, y, CW, actionBoxH, 3, 3, 'FD');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BROWN);
  doc.text(bannerTitle, ML + 6, y + 8);
  doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
  doc.text(actionLines, ML + 6, y + 16);

  const footerY = PAGE_H - 14;
  doc.setFillColor(...CREAM);
  doc.rect(0, footerY, PAGE_W, 14, 'F');
  doc.setFillColor(...route.brandColor);
  doc.rect(0, footerY, PAGE_W, 0.8, 'F');
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...GRAY);
  const footerLeft = isMillwork ? 'SD Millwork' : 'The Window Place LLC';
  const footerMid  = '  \u2022  Office Intake System  \u2022  Internal use only';
  doc.text(footerLeft + footerMid, PAGE_W / 2, footerY + 5.5, { align: 'center' });
  doc.setTextColor(...BROWN_LIGHT);
  doc.text(intake.id || '', PAGE_W / 2, footerY + 10.5, { align: 'center' });

  return doc.output('blob');
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════
export async function generateIntakePDF(intake) {
  if (intake.inquiryType === 'repair') {
    return generateRepairPDF(intake);
  }
  return generateStandardIntakePDF(intake);
}

export async function printIntakeAsPDF(intake) {
  const blob = await generateIntakePDF(intake);
  const url  = URL.createObjectURL(blob);

  const existing = document.getElementById('_twp_intake_print_frame');
  if (existing) existing.parentNode.removeChild(existing);

  const iframe = document.createElement('iframe');
  iframe.id = '_twp_intake_print_frame';
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:none;top:-9999px;left:-9999px;opacity:0;pointer-events:none;';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 300);
    iframe.contentWindow.onafterprint = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      URL.revokeObjectURL(url);
    };
  };
}